import { Link } from "react-router-dom";
import { Container, FadeIn, SectionDivider } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import ProgrammeCard from "@/components/ProgrammeCard";
import StayConnected from "@/components/StayConnected";
import { ArrowRight, CheckCircle2 } from "lucide-react";

/**
 * Reusable audience hub layout used by /family, /schools and /youth.
 *
 * Six sections: Hero · Identity strip · Programme grid · Trust band ·
 * Conversion form · FAQ. Driven entirely by config so the cinematic
 * feel stays consistent and the team only needs to learn ONE editing
 * pattern in the CMS.
 */
export default function AudienceHubPage({
  testIdRoot,
  seo,
  hero,
  identity,
  programmes,
  trustLine,
  faqs,
  finalCta,
  pageSource,
  stayWhatsAppCopy,
}) {
  return (
    <div data-testid={`${testIdRoot}-page`} className="bg-black">
      <SEOHead {...seo} />

      {/* HERO */}
      <section
        data-testid={`${testIdRoot}-hero`}
        className="relative pt-36 pb-24 sm:pt-44 sm:pb-32 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${hero.image})`,
            backgroundPosition: hero.focal || "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <Container className="relative z-10">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
              {hero.eyebrow}
            </p>
            <h1 className="mt-5 font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl">
              {hero.headline}
            </h1>
            <p className="mt-6 max-w-xl text-zinc-300 text-base sm:text-lg leading-relaxed">
              {hero.subhead}
            </p>
            {hero.primaryCta && (
              <Link
                to={hero.primaryCta.to}
                data-testid={`${testIdRoot}-hero-cta`}
                className="mt-9 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
              >
                {hero.primaryCta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </FadeIn>
        </Container>
      </section>

      {/* IDENTITY STRIP */}
      {identity && (
        <section className="relative py-16 sm:py-24 border-y border-amber-500/10">
          <Container>
            <FadeIn>
              <div className="max-w-3xl">
                <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
                  {identity.eyebrow}
                </p>
                <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
                  {identity.headline}
                </h2>
                <p className="mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed">
                  {identity.body}
                </p>
              </div>
            </FadeIn>
          </Container>
        </section>
      )}

      {/* PROGRAMMES GRID */}
      <SectionDivider label="What's inside" />
      <section
        data-testid={`${testIdRoot}-programmes`}
        className="relative py-16 sm:py-24"
      >
        <Container>
          <FadeIn>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {programmes.map((p, i) => (
                <FadeIn key={p.id} delay={i * 0.04}>
                  <ProgrammeCard
                    title={p.title}
                    desc={p.desc}
                    image={p.image}
                    focal={p.focal}
                    status={p.status}
                    href={p.href}
                    eyebrow={p.eyebrow}
                    testId={`${testIdRoot}-programme-${p.id}`}
                  />
                </FadeIn>
              ))}
            </div>
          </FadeIn>
        </Container>
      </section>

      {/* TRUST BAND */}
      {trustLine && (
        <section className="relative py-12 border-y border-white/5 bg-zinc-950">
          <Container>
            <FadeIn>
              <p className="text-center text-sm sm:text-base text-zinc-400 max-w-3xl mx-auto leading-relaxed">
                {trustLine}
              </p>
            </FadeIn>
          </Container>
        </section>
      )}

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <>
          <SectionDivider label="Common questions" />
          <section className="relative py-16 sm:py-24" data-testid={`${testIdRoot}-faq`}>
            <Container>
              <FadeIn>
                <div className="max-w-3xl">
                  <h2 className="font-display font-black uppercase text-3xl sm:text-4xl leading-[0.95]">
                    Frequently asked
                  </h2>
                  <ul className="mt-10 space-y-8">
                    {faqs.map((f, i) => (
                      <li key={i} className="border-l-2 border-amber-400/50 pl-5">
                        <h3 className="font-display font-bold uppercase text-base text-amber-300 tracking-wide">
                          {f.q}
                        </h3>
                        <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                          {f.a}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            </Container>
          </section>
        </>
      )}

      {/* FINAL CTA */}
      {finalCta && (
        <section className="relative py-20 sm:py-28 overflow-hidden">
          <Container>
            <FadeIn>
              <div className="border border-amber-400/30 bg-zinc-950 p-10 sm:p-14 text-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                  {finalCta.eyebrow}
                </p>
                <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
                  {finalCta.headline}
                </h2>
                <p className="mt-5 text-zinc-400 text-base max-w-xl mx-auto">
                  {finalCta.body}
                </p>
                <Link
                  to={finalCta.to}
                  data-testid={`${testIdRoot}-final-cta`}
                  className="mt-8 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
                >
                  {finalCta.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {finalCta.bullets && (
                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto">
                    {finalCta.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-xs text-zinc-300">
                        <CheckCircle2 className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </FadeIn>
          </Container>
        </section>
      )}

      <StayConnected
        variant="whatsapp"
        pageSource={pageSource}
        testid={`${testIdRoot}-stay`}
        whatsappHeadline={stayWhatsAppCopy?.headline || "STAY CLOSE TO THE FIRE"}
        whatsappTagline={stayWhatsAppCopy?.tagline}
        bullets={stayWhatsAppCopy?.bullets}
      />
    </div>
  );
}
